import moment, {Moment} from 'moment'
import _ from 'lodash'
import Component from 'vue-class-component'
import { Line } from 'vue-chartjs'
import {mixins} from 'vue-class-component'
import {ChartData, ChartDataSets, ChartOptions} from 'chart.js';
import {Prop, Watch} from 'vue-property-decorator';
import {AxiosInstance} from 'axios';
import { fetch } from '@/base-http';

/**
 * Define los intervalos de tiempo que puede utilizar el usuario para visualizar
 * la información del número de registros
 */
export enum IntervalOptions {
    // Hourly = 'hourly',
    Daily = 'daily',
    Monthly = 'monthly',
    Annual = 'annual',
}


/**
 * Muestra al usuario estadísticas sobre el registro de usuarios
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component
export default class UserRegistrationChart extends mixins(Line) {
    public axios: AxiosInstance
    /**
     * Intervalo de tiempo por el cual mostrar las fechas de registro
     */
    @Prop({type: String, default: IntervalOptions.Annual})
    public interval: IntervalOptions

    @Prop({type: Boolean, default: false})
    public displayGrid: boolean

    public statData: Array<{registration_date: string | number, registered_users: number}> = []

    public date = new Date()

    public chartData: ChartData = {
        labels: [],
        datasets: [],
    }

    /**
     * Opciones para la visualización de la la gráfica
     */
    public options: ChartOptions = {
        elements: {
            point: {
                radius: 7,
                borderColor: 'white',
                backgroundColor: 'grey',
                borderWidth: 5,
            },
        },
        legend: {
            display: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{gridLines: {display: false}}],
            xAxes: [{gridLines: {display: false}}],
        },
    }

    @Watch('displayGrid')
    public onDisplayGrid (val: boolean) {
        this.toggleChartLines(val)
    }

    /**
     * Activa o desactiva las lineas de los ejes de la gráfica
     *
     * @param active
     */
    public toggleChartLines (active: boolean) {
        this.options.scales!.xAxes![0].gridLines!.display = active
        this.options.scales!.yAxes![0].gridLines!.display = active
        this.renderChart(this.chartData, this.options)
    }

    /**
     * Calcula la escala de tiempo
     */
    public get timeScale (): Moment[] {
        const date = moment(this.date)
        switch (this.interval) {
            case IntervalOptions.Monthly:
                return _.range(0, 12).map(m => date.clone().month(m))
            case IntervalOptions.Annual:
                if (this.statData.length === 0) {
                    return []
                }

                const firstYear = this.statData[0].registration_date as number
                return _.range(
                    firstYear - 1,
                    (this.statData[this.statData.length - 1].registration_date as number) + 1,
                ).map(y => moment().year(y))
            case IntervalOptions.Daily:
                return _.range(0, moment().daysInMonth()).map(d => date.clone().date(d))
            default:
                return []
        }
    }

    /**
     *
     */
    public get timeLabels () {
        const formats = {
            [IntervalOptions.Annual]: 'YYYY',
            [IntervalOptions.Monthly]: 'MMM',
            [IntervalOptions.Daily]: 'DD',
        }

        const format = formats[this.interval]

        return this.timeScale.map(t => t.format(format))
    }

    /**
     * Watcher para propiedad interval
     *
     * Observa la propiedad "interval", la cual indica al componente que intervalo de
     * tiempo ha seleccionado el usuario
     *
     * Cuando el intervalo cambia, se solicita la estadística al servidor, una vez
     * obtenida se recalculan las escalas de tiempo, las etiquetas de la gráfica
     * y el conjunto de datos
     */
    @Watch('interval')
    public async onInterval () {
        // Solicita al servidor la estadística y guarda el resultado en el estado del componente
        await this.loadStatData()
        this.chartData.labels = this.timeLabels
        this.chartData.datasets![0].data = this.calculateChartData()
        this.update()
    }

    /**
     * Al montar el componente renderizá la gráfica
     */
    public async mounted () {
        await this.loadStatData()
        this.chartData = {
            labels: this.timeLabels,
            datasets: [{
                backgroundColor: ['rgba(0,0,0,0)'],
                label: 'Usuario registrados',
                data: this.calculateChartData(),
            }],
        }
        this.renderChart(this.chartData, this.options)
    }

    public calculateChartData () {
        // Función de mapea para el intervalo anual
        const dataProcessors = {
            [IntervalOptions.Monthly]: (scale: Moment) => {
                const set = this.statData.find(sd => sd.registration_date === scale.format('MM-YYYY'))
                if (!set) {
                    return 0
                }

                return set.registered_users
            },
            [IntervalOptions.Annual]: (scale: Moment) => {
                const set = this.statData.find(sd => sd.registration_date === scale.year())
                if (!set) {
                    return 0
                }

                return set.registered_users
            },
            [IntervalOptions.Daily]: (scale: Moment) => {
                const set = this.statData.find(sd => sd.registration_date === scale.format('DD-MM-YYYY'))
                if (!set) {
                    return 0
                }

                return set.registered_users
            },
        }

        return this.timeScale.map(dataProcessors[this.interval])
    }

    /**
     * Actualizar gráfica
     *
     * Una vez realizados los cambios en chartDate se debe llamar manualmente a este método
     * para actualizar la gráfica y que las modificaciones se muestren.
     *
     * Nota: Existe un mixin de vue-charts llamado reactiveData, el cual define la propiedad
     * chartData y un watcher que re-pintará la gráfica cada vez que la información
     * cambie.
     *
     * Sin embargo, no es usado porque su funcionamiento es irregular y no reactivo al
     * completo cuando se utilizan propiedades computadas.
     */
    public update () {
        this.$nextTick(() => this.$data._chart.update())
    }

    /**
     * Solicitar información de estadísticas
     */
    public async loadStatData () {
        this.$emit('loading', true)

        const params = {
            interval: this.interval,
        }

        this.statData = (await fetch('/management/users/stats/registration', {params})).stats

        this.$emit('loading', false)
    }
}
